import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { MICRO_TRANSITION_TIMING } from 'HubStyleTokens/times';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import UIClickable from '../button/UIClickable';
import SyntheticEvent from '../core/SyntheticEvent';
import Controllable from '../decorators/Controllable';
import UIDragHandle from '../draggable/UIDragHandle';
import H7 from '../elements/headings/H7';
import { CARET_HEIGHT, CARET_WIDTH } from '../icon/IconConstants';
import UICaret from '../icon/icons/UICaret';
import HoverProvider from '../providers/HoverProvider';
import AccordionTransition from '../transitions/AccordionTransition';
import { hidden } from '../utils/propTypes/decorators';
import { getComponentPropType } from '../utils/propTypes/componentProp';
import { EXPANDABLE_ACTIONS_SPACING_X, EXPANDABLE_DARK_BACKGROUND, EXPANDABLE_DARK_BORDER_COLOR, EXPANDABLE_OPENER_COLOR, EXPANDABLE_OPENER_DISABLED_COLOR, EXPANDABLE_OPENER_HOVER_COLOR, EXPANDABLE_PADDING_LEFT, EXPANDABLE_PADDING_X } from './ExpandableConstants';
import UIExpandableContents from './UIExpandableContents';
const indentationPropType = PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['default', 'compact', 'none'])]);
const USE_OPTIONS = ['default', 'enclosed'];
const getEnclosedStyle = css(["background-color:", ";border-width:1px 0;margin-bottom:-1px;"], EXPANDABLE_DARK_BACKGROUND);
const ExpandableActions = styled.div.withConfig({
  displayName: "UIExpandableSection__ExpandableActions",
  componentId: "syyobq-0"
})(["align-items:center;display:flex;margin-left:", ";*:first-child > * + *{margin-left:", ";}"], EXPANDABLE_ACTIONS_SPACING_X, EXPANDABLE_ACTIONS_SPACING_X);
const ExpandableWrapper = styled.div.withConfig({
  displayName: "UIExpandableSection__ExpandableWrapper",
  componentId: "syyobq-1"
})([""]);
const ExpandableHeader = styled.div.withConfig({
  displayName: "UIExpandableSection__ExpandableHeader",
  componentId: "syyobq-2"
})(["border-color:", ";border-style:solid;border-width:1px 0 0 0;display:flex;justify-content:space-between;padding-left:0;padding-right:", ";position:relative;", ";&::after{content:'';height:54px;margin-left:-1px;width:1px;}"], EXPANDABLE_DARK_BORDER_COLOR, EXPANDABLE_PADDING_X, props => props.use === 'enclosed' ? getEnclosedStyle : null);
ExpandableHeader.propTypes = {
  use: PropTypes.oneOf(USE_OPTIONS)
};
const ExpandableButton = styled(UIClickable).withConfig({
  displayName: "UIExpandableSection__ExpandableButton",
  componentId: "syyobq-3"
})(["color:", ";align-items:center;display:flex;flex:1 1 auto;padding:10px 0;transition:color ", " ease-out;user-select:none;&:hover:not([aria-disabled='true']){color:", ";}"], props => props.disabled ? EXPANDABLE_OPENER_DISABLED_COLOR : undefined, MICRO_TRANSITION_TIMING, EXPANDABLE_OPENER_HOVER_COLOR);
const ExpandableTitle = styled(({
  className,
  titleText
}) =>
/*#__PURE__*/

/*
 * Here, we need to null out the `aria-level` otherwise it defaults to `2`,
 * so we disable the lint rule to allow it to be a non-integer. Then we need
 * to disable the rule to allow `aria-level` to be there with `presentation`
 * since the <H7> component has a default of `role="heading"`.
 * */

/* eslint-disable jsx-a11y/role-supports-aria-props */

/* eslint-disable jsx-a11y/aria-proptypes */
_jsx(H7, {
  "aria-level": undefined,
  className: className,
  role: "presentation",
  children: titleText
})
/* eslint-enable jsx-a11y/aria-proptypes */

/* eslint-enable jsx-a11y/role-supports-aria-props */
).withConfig({
  displayName: "UIExpandableSection__ExpandableTitle",
  componentId: "syyobq-4"
})(["margin-bottom:0;"]);
ExpandableTitle.propTypes = {
  titleText: PropTypes.node
};
const ExpandableCaretHolder = styled.span.withConfig({
  displayName: "UIExpandableSection__ExpandableCaretHolder",
  componentId: "syyobq-5"
})(["display:flex;flex-shrink:0;justify-content:center;width:", ";"], EXPANDABLE_PADDING_LEFT);
const ExpandableCaret = styled(UICaret).attrs({
  color: EXPANDABLE_OPENER_COLOR,
  viewBoxHeight: CARET_HEIGHT,
  viewBoxWidth: CARET_WIDTH,
  height: 12,
  width: 5
}).withConfig({
  displayName: "UIExpandableSection__ExpandableCaret",
  componentId: "syyobq-6"
})(["transition:all ", " ease-in-out;transform:", ";"], MICRO_TRANSITION_TIMING, props => props.open ? 'rotate(90deg)' : null);
const ExpandableDragHandleHolder = styled.span.withConfig({
  displayName: "UIExpandableSection__ExpandableDragHandleHolder",
  componentId: "syyobq-7"
})(["left:8px;opacity:", ";position:absolute;top:50%;transform:translateY(-50%);transition:opacity ", " ease-out;"], props => props.hovered ? '1' : '0', MICRO_TRANSITION_TIMING);

const DefaultContentWrapper = props => {
  const {
    open,
    children,
    className,
    indentation,
    use
  } = props;
  return /*#__PURE__*/_jsx(UIExpandableContents, {
    "aria-hidden": open ? 'false' : 'true',
    className: className,
    indentation: indentation,
    use: use,
    children: children
  });
};

DefaultContentWrapper.propTypes = {
  open: PropTypes.bool,
  children: PropTypes.node,
  indentation: indentationPropType,
  use: PropTypes.oneOf(USE_OPTIONS)
};

const DefaultCaret = props => {
  const {
    open
  } = props;
  return /*#__PURE__*/_jsx(ExpandableCaret, {
    open: open
  });
};

const defaultProps = {
  Caret: DefaultCaret,
  ContentWrapper: DefaultContentWrapper,
  disabled: false,
  draggable: false,
  DragHandle: UIDragHandle,
  Header: ExpandableHeader,
  indentation: 'default',
  open: false,
  Opener: ExpandableButton,
  Title: ExpandableTitle,
  use: 'default'
};

const UIExpandableSection = props => {
  const {
    actions,
    Caret = defaultProps['Caret'],
    children,
    contentClassName,
    ContentWrapper = defaultProps['ContentWrapper'],
    disabled = defaultProps['disabled'],
    DragHandle = defaultProps['DragHandle'],
    draggable = defaultProps['draggable'],
    Header = defaultProps['Header'],
    indentation = defaultProps['indentation'],
    onOpenChange,
    open = defaultProps['open'],
    Opener = defaultProps['Opener'],
    titleText,
    Title = defaultProps['Title'],
    use = defaultProps['use']
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["actions", "Caret", "children", "contentClassName", "ContentWrapper", "disabled", "DragHandle", "draggable", "Header", "hovered", "indentation", "onOpenChange", "open", "Opener", "titleText", "Title", "use"]);

  return /*#__PURE__*/_jsx(HoverProvider, Object.assign({}, props, {
    children: hoverProviderProps => {
      const {
        hovered
      } = hoverProviderProps,
            hoverProviderRestProps = _objectWithoutPropertiesLoose(hoverProviderProps, ["hovered"]);

      return /*#__PURE__*/_jsxs(ExpandableWrapper, Object.assign({}, rest, {}, hoverProviderRestProps, {
        children: [/*#__PURE__*/_jsxs(Header, {
          use: use,
          children: [draggable ? /*#__PURE__*/_jsx(ExpandableDragHandleHolder, {
            hovered: hovered,
            children: /*#__PURE__*/_jsx(DragHandle, {})
          }) : null, titleText ? /*#__PURE__*/_jsxs(Opener, {
            "aria-expanded": open ? 'true' : 'false',
            disabled: disabled,
            onClick: onOpenChange && (() => {
              onOpenChange(SyntheticEvent(!open));
            }),
            children: [/*#__PURE__*/_jsx(ExpandableCaretHolder, {
              children: /*#__PURE__*/_jsx(Caret, {
                open: open
              })
            }), /*#__PURE__*/_jsx(Title, {
              titleText: titleText
            })]
          }) : null, actions ? /*#__PURE__*/_jsx(ExpandableActions, {
            children: actions
          }) : null]
        }), children ? /*#__PURE__*/_jsx(AccordionTransition, {
          open: open,
          children: /*#__PURE__*/_jsx(ContentWrapper, {
            open: open,
            className: contentClassName,
            indentation: indentation,
            use: use,
            children: children
          })
        }) : null]
      }));
    }
  }));
};

UIExpandableSection.propTypes = {
  actions: PropTypes.node,
  children: PropTypes.node,
  Caret: PropTypes.elementType,
  contentClassName: PropTypes.string,
  ContentWrapper: getComponentPropType(DefaultContentWrapper).isRequired,
  disabled: PropTypes.bool.isRequired,
  draggable: PropTypes.bool.isRequired,
  DragHandle: PropTypes.elementType,
  Header: getComponentPropType(ExpandableHeader).isRequired,
  hovered: hidden(PropTypes.bool),
  indentation: indentationPropType,
  onOpenChange: PropTypes.func,
  open: PropTypes.bool,
  Opener: PropTypes.elementType,
  titleText: ExpandableTitle.propTypes.titleText,
  Title: getComponentPropType(ExpandableTitle).isRequired,
  use: PropTypes.oneOf(USE_OPTIONS)
};
UIExpandableSection.defaultProps = defaultProps;
UIExpandableSection.displayName = 'UIExpandableSection';
export default Controllable(UIExpandableSection, ['open']);